import { TriggerError } from './error'
import type { Trigger } from './index'

/** Wait `ms`, or until the screen goes away. */
function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new TriggerError('aborted'))
    const id = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(id)
      reject(new TriggerError('aborted'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * The 'timed' frame plan: one picture at each offset of `profile.timingsMs`, counted from the moment the face is in
 * place (the screen calls the trigger right after the token arrives). The offsets never need to be near
 * CAPTURE_MIN_MS: the screen holds the upload until the minimum has passed.
 */
export const timed: Trigger = async ({ grab, profile, signal }) => {
  const timings = profile.timingsMs ?? []
  const start = Date.now()
  const frames: Blob[] = []
  for (const at of timings) {
    const delay = start + at - Date.now()
    if (delay > 0) await wait(delay, signal)
    else if (signal.aborted) throw new TriggerError('aborted')
    frames.push(await grab())
  }
  return frames
}
